import { useState, useEffect, useCallback } from 'react';
import { fetchEvents, addEvent, deleteEvent } from '../firebase/events';
import './Events.css';

const ADMIN_PASSWORD = 'Elf2017!!!';

const CATEGORIES = ['All', 'Activities', 'Awareness', 'Workshop', 'Social', 'Sports', 'Fundraiser'];

const categoryColors = {
  Activities: 'mint', Awareness: 'lavender', Workshop: 'blue',
  Social: 'peach', Sports: 'mint', Fundraiser: 'peach',
};

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(timeStr) {
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${m} ${ampm}`;
}

export default function Events() {
  const [events, setEvents]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [filter, setFilter]           = useState('All');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isAdmin, setIsAdmin]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [form, setForm]               = useState({
    name: '', date: '', time: '', location: '', description: '', category: 'Activities', emoji: '🌟',
  });
  const [formError, setFormError]     = useState('');

  // ── Load events from Firestore ─────────────────────────────
  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchEvents();
      setEvents(data);
    } catch (err) {
      console.error(err);
      setError('Couldn\'t load events right now. Please try again in a moment! 🌸');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const filtered = filter === 'All' ? events : events.filter(e => e.category === filter);
  const sorted   = [...filtered].sort((a, b) => new Date(a.date) - new Date(b.date));

  // ── Admin auth ─────────────────────────────────────────────
  function handleAdminClick() {
    if (isAdmin) { setShowAddForm(true); }
    else         { setShowPasswordModal(true); }
  }

  function handlePassword(e) {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAdmin(true);
      setShowPasswordModal(false);
      setPasswordInput('');
      setPasswordError('');
      setShowAddForm(true);
    } else {
      setPasswordError('Oops! That password isn\'t right. Try again! 🔑');
    }
  }

  // ── Add event ──────────────────────────────────────────────
  function handleFormChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleAddEvent(e) {
    e.preventDefault();
    if (!form.name || !form.date || !form.time || !form.location || !form.description) {
      setFormError('Please fill in all the fields! 📝');
      return;
    }
    setSaving(true);
    try {
      const id = await addEvent(form);
      setEvents(prev => [...prev, { ...form, id }].sort((a,b) => new Date(a.date)-new Date(b.date)));
      setForm({ name:'', date:'', time:'', location:'', description:'', category:'Activities', emoji:'🌟' });
      setShowAddForm(false);
      setFormError('');
    } catch (err) {
      console.error(err);
      setFormError('Something went wrong saving the event. Try again! 😔');
    } finally {
      setSaving(false);
    }
  }

  // ── Delete event ───────────────────────────────────────────
  async function handleDeleteEvent(id) {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error(err);
      alert('Couldn\'t delete the event. Try again!');
    }
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="events fade-in">
      <section className="events-hero">
        <div className="container">
          <h1 className="section-title">📅 Events & Activities</h1>
          <p className="section-subtitle">
            Find fun, inclusive events for kids of all abilities and their families! 🌟
          </p>
          <button className="btn btn-primary add-event-btn" onClick={handleAdminClick}>
            {isAdmin ? '➕ Add New Event' : '🔑 Add Event (Admin)'}
          </button>
        </div>
      </section>

      <div className="container events-body">
        {/* Filter */}
        <div className="events-filter">
          <span className="filter-label">Filter by:</span>
          <div className="filter-chips">
            {CATEGORIES.map(cat => (
              <button key={cat}
                className={`filter-chip ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >{cat}</button>
            ))}
          </div>
        </div>

        {/* States */}
        {loading && (
          <div className="events-loading">
            <span className="loading-spinner">🌸</span>
            <p>Loading events…</p>
          </div>
        )}

        {error && !loading && (
          <div className="events-error">
            <span>😔</span>
            <p>{error}</p>
            <button className="btn btn-outline" onClick={loadEvents}>Try Again</button>
          </div>
        )}

        {/* Events grid */}
        {!loading && !error && (
          sorted.length === 0 ? (
            <div className="no-events">
              <span>📭</span>
              <p>No events in this category yet. Check back soon!</p>
            </div>
          ) : (
            <div className="events-grid">
              {sorted.map(event => {
                const color  = categoryColors[event.category] || 'lavender';
                const eventEnd = new Date(event.date); eventEnd.setHours(23, 59, 59);
                const isPast = eventEnd < new Date();
                return (
                  <div key={event.id} className={`event-card card event-card-${color} ${isPast ? 'past' : ''}`}>
                    <div className="event-emoji-row">
                      <span className="event-main-emoji">{event.emoji}</span>
                      <span className={`event-category-tag tag tag-${color}`}>{event.category}</span>
                      {isPast && <span className="past-tag">Past</span>}
                      {isAdmin && (
                        <button className="delete-btn" onClick={() => handleDeleteEvent(event.id)}
                          title="Delete event" aria-label="Delete event">✕</button>
                      )}
                    </div>
                    <h3 className="event-name">{event.name}</h3>
                    <div className="event-details">
                      <div className="event-detail"><span>📅</span><span>{formatDate(event.date)}</span></div>
                      <div className="event-detail"><span>⏰</span><span>{formatTime(event.time)}</span></div>
                      <div className="event-detail"><span>📍</span><span>{event.location}</span></div>
                    </div>
                    <p className="event-desc">{event.description}</p>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPasswordModal(false)}>✕</button>
            <span className="modal-emoji">🔑</span>
            <h3>Admin Access</h3>
            <p>Only Emmi can add events! Enter the password:</p>
            <form onSubmit={handlePassword} className="password-form">
              <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)}
                placeholder="Enter password..." className="form-input" autoFocus />
              {passwordError && <p className="form-error">{passwordError}</p>}
              <button type="submit" className="btn btn-primary">Enter 🔓</button>
            </form>
          </div>
        </div>
      )}

      {/* Add Event Form */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="modal modal-large card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAddForm(false)}>✕</button>
            <span className="modal-emoji">✨</span>
            <h3>Add a New Event</h3>
            <form onSubmit={handleAddEvent} className="add-event-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Event Name *</label>
                  <input name="name" value={form.name} onChange={handleFormChange}
                    placeholder="e.g. Inclusive Fun Day" className="form-input" />
                </div>
                <div className="form-group">
                  <label>Emoji</label>
                  <input name="emoji" value={form.emoji} onChange={handleFormChange}
                    placeholder="🌟" className="form-input" maxLength={2} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input name="date" type="date" value={form.date} onChange={handleFormChange} className="form-input" />
                </div>
                <div className="form-group">
                  <label>Time *</label>
                  <input name="time" type="time" value={form.time} onChange={handleFormChange} className="form-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Location *</label>
                <input name="location" value={form.location} onChange={handleFormChange}
                  placeholder="e.g. City Park, Main Pavilion" className="form-input" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select name="category" value={form.category} onChange={handleFormChange} className="form-input">
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea name="description" value={form.description} onChange={handleFormChange}
                  placeholder="Tell everyone what this event is about!" className="form-input form-textarea" rows={3} />
              </div>
              {formError && <p className="form-error">{formError}</p>}
              <div className="form-buttons">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving… ⏳' : 'Add Event 🎉'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
